import { AuthenticationStrategy } from './auth';
import { requestInterceptorOptions, requestInterceptorResponse } from './interceptor';
import { Logger } from './log';
import { RetryInfo, RetryStrategy } from './retry';
import neverRetryStrategy from './retry/strategy/never';
import { BaseRequestOptions, BaseResponseOptions } from './types';

/**
 * A type for Fetch where the RequestInit (request options) and Response are generic. This
 * is necessary because the native TypeScript fetch definition from lib.dom.d.ts and the
 * node-fetch type definition use mutually exclusive type definitions for their
 * signatures.
 *
 * It is not intended that this be used directly unless absolutely necessary.
 *
 * Note the Request alternative of the first arg is not supported here, as (1) it is
 * not expected to be widely used and (2) it would require all Fetch definitions to
 * have a third generic.
 */
export type Fetch<Req extends BaseRequestOptions, Res extends BaseResponseOptions> = (
  url: string,
  init?: Req,
) => Promise<Res>;

type Fetcher<Res extends BaseResponseOptions> = () => Promise<Res>;

/**
 * Adds headers that are typically required to interact with TRP services.
 *
 * @param options
 */
function applyRequiredHeaders<R extends BaseRequestOptions>(options: R) {
  if (!options.headers) {
    options.headers = {};
  }
  options.headers['X-TRP-Requested-With'] = 'nitro-fetch';
  return options;
}

/**
 * Determines if the request should be made again based on the RetryStrategy
 * passed. Assumes there's always a RetryStrategy.
 *
 * @param retryStrategy
 * @param response
 * @param retryInfo
 */
async function shouldRetry<R extends BaseResponseOptions>(
  retryStrategy: RetryStrategy<R>,
  response: R,
  retryInfo: RetryInfo,
) {
  const b = retryStrategy(response, retryInfo);
  if (typeof b === 'boolean') {
    return Promise.resolve(b);
  }
  return b;
}

// Builder
// =======
// Wraps a fetch (ex: window.fetch, whatwg-fetch, node-fetch) in various useful
// layers and returns a fetch-like thing to be used. The intent here is to not
// inhibit the behavior of fetch

export interface Builder<
  Req extends BaseRequestOptions,
  Res extends BaseResponseOptions,
> {
  // the response is explicitly joined with the base options so literals can be used, otherwise excess property
  // checking throws an error
  build: () => Fetch<Req & BaseRequestOptions, Res & BaseResponseOptions>;
  withAuthStrategy: (strategy: AuthenticationStrategy<Req, Res>) => Builder<Req, Res>;
  withRetryStrategy(retryStrategy: RetryStrategy<Res>): Builder<Req, Res>;
}

/**
 * The builder itself follows a typical builder pattern where each public function (aside
 * from build) builds up state configuring how fetch works. build() returns a fetch-like
 * function that uses that state to configure the request before its passed on to the
 * real fetch library.
 */
class FetchBuilder<Req extends BaseRequestOptions, Res extends BaseResponseOptions>
  implements Builder<Req, Res>
{
  private readonly fetch: Fetch<Req, Res>;
  private authenticationStrategy: AuthenticationStrategy<Req, Res> | null = null;
  private retryStrategy: RetryStrategy<Res> = neverRetryStrategy;

  constructor(fetch: Fetch<Req, Res>) {
    this.fetch = fetch;
  }

  /**
   * Returns a Fetcher built with the passed url and options. A Fetcher is just a function
   * that will execute the same fetch() every time it's called.
   *
   * @param url
   * @param options
   */
  private buildFetcher(url: string, options: Req): Fetcher<Res> {
    // common headers are applied universally right now, if this is a problem it can be parameterized
    options = applyRequiredHeaders(options);
    return async () => {
      // as additional interceptors are implemented, this logic will probably be best served
      // by reducing over an ordered list
      options = await requestInterceptorOptions(
        this.authenticationStrategy,
        options,
        url,
      );
      const response = await this.fetch(url, options);
      response.log = options.log;
      return requestInterceptorResponse(this.authenticationStrategy, response);
    };
  }

  /**
   * Executes the actual fetch using the configured fetch implementation, potentially
   * multiple times.
   *
   * @param fetcher
   * @param url
   * @param method
   * @param log
   */
  private async fetchWithRetry(
    fetcher: Fetcher<Res>,
    url: string,
    method: string | undefined,
    log?: Logger,
  ) {
    let attempts = 0;
    let response: Res;
    do {
      attempts += 1;
      log?.info(`[${method || 'GET'}] fetch from ${url}.`);
      response = await fetcher();
    } while (await shouldRetry(this.retryStrategy, response, { attempts, log }));
    return response;
  }

  /**
   * Returns a fetch-like function that can be used with real fetch options. The options
   * that would normally be passed to a fetch implementation (ex: node-fetch) are decorated
   * before being passed to the implementation.
   */
  build(): Fetch<Req & BaseRequestOptions, Res & BaseResponseOptions> {
    return async (url, options) => {
      options = Object.assign({}, options);
      const fetcher = this.buildFetcher(url, options);
      return this.fetchWithRetry(fetcher, url, options.method, options.log);
    };
  }

  /**
   * Configures an AuthenticationStrategy to be used with any fetch call returned from build().
   *
   * @param authenticationStrategy
   */
  withAuthStrategy(
    authenticationStrategy: AuthenticationStrategy<Req, Res>,
  ): FetchBuilder<Req, Res> {
    this.authenticationStrategy = authenticationStrategy;
    return this;
  }

  /**
   * Configures an RetryStrategy to be used with any fetch call returned from build().
   *
   * @param retryStrategy
   */
  withRetryStrategy(retryStrategy: RetryStrategy<Res>): FetchBuilder<Req, Res> {
    this.retryStrategy = retryStrategy;
    return this;
  }
}

// overloads to support either a fully typed signature (inferred from the passed fetch implementation) or the built-in
// fetch implementation (available in the browser or Node.js 18)
export function initFetch<
  Req extends BaseRequestOptions,
  Res extends BaseResponseOptions,
>(fetchImpl: Fetch<Req, Res>): Builder<Req, Res>;
export function initFetch(): Builder<RequestInit, Response>;

/**
 * Initializes a fetch builder with the passed fetch implementation. Once
 * options are configured via the builder, build() should be called to return
 * a configured fetch-like function that uses the configured strategies.
 *
 * The generics are intended to be automatically inferred from the passed fetch
 * implementation, ensuring that what comes out of the builder is usable in the same way
 * as what's passed in.
 *
 * If a fetch implementation is not passed, the global fetch implementation is used, if
 * available.
 *
 * See: https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API
 *
 * @param fetchImpl
 */
export function initFetch<
  Req extends BaseRequestOptions,
  Res extends BaseResponseOptions,
>(fetchImpl?: Fetch<Req, Res>): Builder<Req, Res> {
  if (!fetchImpl) {
    // because fetch would not then be called on globalThis (ex: globalThis.fetch()), it is bound
    // to globalThis to be used as a bare function, just in case
    //
    // the cast to any is necessary because fetch<RequestInit, Response> doesn't bubble up and set
    // the generic parameters Req and Res...it can be assumed that standard fetch is compatible
    // with the default overload
    fetchImpl = globalThis.fetch.bind(globalThis) as any;
  }
  if (!fetchImpl) {
    throw new Error(
      'Missing required implementation of fetch. Use an updated runtime or pass a fetch implementation (ex: node-fetch, whatwg-fetch).',
    );
  }
  return new FetchBuilder(fetchImpl);
}
